import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { useRef } from 'react';
import { ApiConfig } from '@/config/api.config';
import OwnInnIcon from '../icons/OwnInnIcon';
import { ImagesHelper } from '@/utils/ImagesHelper';

type Props<T extends {}> = {
	label?: string;
	optional?: boolean;
	type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
} & FormInputProps<T>;

const ELogoUploaderForm = <T extends {}>({
	label,
	optional = false,
	fieldName,
	control,
	className,
	containerClassName,
	disabled,
	placeholder = 'add logo',
}: Props<T>) => {
	const inputRef = useRef<HTMLInputElement | null>(null);

	const handleInputRef = () => {
		if (inputRef.current) inputRef.current.click();
	};

	return (
		<div
			className={cn(
				'w-full flex flex-col gap-1.5 select-none',
				containerClassName,
			)}>
			{label && (
				<h4 className='text-sm font-medium'>
					{label}
					{optional && (
						<span className='text-popover-600'> (optional)</span>
					)}
				</h4>
			)}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange },
					fieldState,
					formState,
				}) => {
					const handleChangeFile = (file: File) => {
						if (file) {
							onChange(file);
						}
					};

					const handleFileInputChange = (
						e: React.ChangeEvent<HTMLInputElement>,
					) => {
						const file = e.target.files?.[0];
						if (file) {
							handleChangeFile(file);
						}
					};

					const typedValue = value as File | string;

					return (
						<>
							<div className='relative'>
								<div
									className={cn(
										'px-3.5 py-3 w-full flex items-center gap-2.5 bg-popover-10 border border-dashed border-popover-100 rounded-semi-xl cursor-pointer select-none overflow-hidden transition-all hover:bg-primary-color-50',
										fieldState.invalid
											? 'border-error-1000'
											: '',
										className,
									)}
									onClick={handleInputRef}>
									{value ? (
										<>
											<img
												src={ImagesHelper.getImageUrlFromFile(
													typedValue,
												)}
												alt='Profile picture'
												className='w-5 h-5 object-cover'
												onClick={handleInputRef}
											/>
											<p className='text-xs font-normal text-popover-800'>
												{typedValue instanceof File
													? typedValue.name
													: ''}
											</p>
										</>
									) : (
										<>
											<OwnInnIcon />
											<p className='text-xs font-normal text-popover-600'>
												{placeholder}
											</p>
										</>
									)}
								</div>
								<input
									type='file'
									className={'hidden'}
									ref={inputRef}
									onChange={handleFileInputChange}
									disabled={disabled || formState.disabled}
								/>
							</div>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</>
					);
				}}
			/>
		</div>
	);
};

export default ELogoUploaderForm;
